import { CalendarEventReminder } from '@griply/types';
import {
  addCalendarEventReminder,
  getCalendarEventReminderLabel,
  MAX_CALENDAR_EVENT_REMINDERS,
  reminderMinuteOptions,
  setCalendarEventReminderMinutes,
} from '@griply/utils';
import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Plus16, X } from 'shared/assets/icons';
import { Button, Colors, Sizes, Variants } from 'shared/components/ui/button';
import {
  CustomFormSelect,
  Size as SelectSize,
} from 'shared/components/ui/custom-form-select';
import {
  IconButton,
  Sizes as IconButtonSizes,
} from 'shared/components/ui/icon-button';

import * as Styled from './form-reminders-input.style';

export type FormRemindersInputProps = {
  // null = the calendar's default reminders apply
  value: CalendarEventReminder[] | null;
  onChange: (reminders: CalendarEventReminder[] | null) => void;
};

export const FormRemindersInput: React.FC<FormRemindersInputProps> = ({
  value,
  onChange,
}) => {
  const { t } = useTranslation();

  const minuteLabel = useMemo(
    () => (minutes: number) => {
      const { key, count } = getCalendarEventReminderLabel(minutes);
      return t(key, { count });
    },
    [t],
  );

  const options = useMemo(
    () =>
      reminderMinuteOptions.map((minutes) => ({
        label: minuteLabel(minutes),
        value: String(minutes),
      })),
    [minuteLabel],
  );

  const onAdd = () => onChange(addCalendarEventReminder(value ?? []));

  return (
    <Styled.Container>
      {value === null && (
        <Styled.Row>
          <Styled.DefaultLabel>
            {t('forms.event.fields.reminders.calendar-default')}
          </Styled.DefaultLabel>
          <IconButton
            icon={Plus16}
            size={IconButtonSizes.Small}
            onClick={onAdd}
            type="button"
            aria-label={t('forms.event.fields.reminders.add')}
          />
        </Styled.Row>
      )}

      {value?.map((reminder, index) => {
        const onSelect = (minutes?: string) =>
          minutes !== undefined &&
          onChange(
            setCalendarEventReminderMinutes(value, index, Number(minutes)),
          );
        // Emptying the list means "no reminders"; the default row is how you get defaults back.
        const onRemove = () => onChange(value.filter((_, i) => i !== index));
        return (
          <Styled.Row key={index}>
            <Styled.RowSelect>
              <CustomFormSelect
                value={String(reminder.minutes)}
                options={options}
                onChange={onSelect}
                size={SelectSize.ExtraSmall}
              />
            </Styled.RowSelect>
            <Button
              variant={Variants.Outlined}
              size={Sizes.Small}
              color={Colors.Inherit}
              startIcon={X}
              onClick={onRemove}
              type="button"
              tabIndex={-1}
            />
          </Styled.Row>
        );
      })}

      {value !== null && value.length < MAX_CALENDAR_EVENT_REMINDERS && (
        <Styled.Row>
          <IconButton
            icon={Plus16}
            size={IconButtonSizes.Small}
            onClick={onAdd}
            type="button"
            aria-label={t('forms.event.fields.reminders.add')}
          />
        </Styled.Row>
      )}
    </Styled.Container>
  );
};
